import { useMemo } from 'react';

import { mapAPIAbsencesToAbsenceEntries } from '~/data/absences/data/mapping';
import { AbsenceEntry, AbsenceType } from '~/data/absences/types';
import { useGetLongAbsences } from '~/data/absences/useGetLongAbsences';
import { useGetShortAbsences } from '~/data/absences/useGetShortAbsences';
import { EnumListKey, useAllEnums } from '~/data/enums';

type PropsType = {
  employeeId: number;
  absenceType: AbsenceType;
  loadHistorical: boolean;
};

type UseEmployeeAbsenceHistoricalDataOutput = {
  isLoading: boolean;
  failedToLoad: boolean;
  absenceEntries: AbsenceEntry[];
};

export const useEmployeeAbsenceHistoricalData = (
  props: PropsType,
): UseEmployeeAbsenceHistoricalDataOutput => {
  const { employeeId, absenceType, loadHistorical } = props;

  const { getEnums } = useAllEnums();

  const swrConfig = {
    revalidateOnFocus: false,
  };

  const longAbsencesSWR = useGetLongAbsences(
    { employeeId, period: 'historical' },
    swrConfig,
    !(absenceType === AbsenceType.LONG && loadHistorical),
  );
  const { data: historicalLongAbsences } = longAbsencesSWR;

  const shortAbsencesSWR = useGetShortAbsences(
    {
      employeeId,
      period: 'historical',
    },
    swrConfig,
    !(absenceType === AbsenceType.SHORT && loadHistorical),
  );
  const { data: historicalShortAbsences } = shortAbsencesSWR;

  const { absenceEntries } = useMemo(() => {
    const absenceReasons = {
      short: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
      long: getEnums(EnumListKey.ABSENCE_LONG_REASON),
    };

    const combinedHistorical = [
      ...(historicalLongAbsences ?? []),
      ...(historicalShortAbsences ?? []),
    ];

    const absenceEntries: AbsenceEntry[] = combinedHistorical
      ? mapAPIAbsencesToAbsenceEntries(
          combinedHistorical,
          absenceReasons.long,
          absenceReasons.short,
        )
      : [];

    return {
      absenceEntries,
    };
  }, [getEnums, historicalLongAbsences, historicalShortAbsences]);

  return {
    isLoading: longAbsencesSWR.isLoading || shortAbsencesSWR.isLoading,
    failedToLoad: longAbsencesSWR.failedToLoad || shortAbsencesSWR.failedToLoad,
    absenceEntries,
  };
};
